import React from 'react';
const trackedEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'platform-api'];
/**
 * A custom React hook that monitors activities within the app and executes appropriate responses.
 * Useful for implementing automatic session timeouts or activity tracking mechanisms.
 *
 * @param {IdleTimeoutParams} params - Configuration object for idle timeout behavior.
 * @param {number} [params.idleTimeoutMs=1800000] - Time in milliseconds before the user is considered idle (default: 30 minutes).
 * @param {number} [params.debounceMs=300000] - Minimum inactivity duration to reset activity (default: 5 minutes).
 * @param {number} [params.monitorDelayMs=60000] - Frequency in milliseconds to check for idle status (default: 1 minute).
 * @param {(elapsedTimeMs: number, reset: () => void) => void} [params.onIdle] - Callback triggered when user is considered idle.
 * @param {() => void} [params.onActive] - Callback triggered when activity is detected after a period of inactivity.
 *
 * @returns {void} This hook does not return a value.
 */
export function useIdleTimeout(params) {
    const { idleTimeoutMs = 30 * 60 * 1000, debounceMs = 5 * 60 * 1000, monitorDelayMs = 60 * 1000, onIdle = () => undefined, onActive = () => undefined, } = params;
    const startTime = React.useRef(Date.now());
    const expectedTime = React.useRef(startTime.current + monitorDelayMs);
    const activityDetected = React.useRef(false);
    const timeoutFired = React.useRef(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const timeOutHandle = React.useRef(null);
    const handleActivity = React.useCallback(() => {
        activityDetected.current = true;
    }, []);
    const handleInactivity = React.useCallback(() => {
        const now = Date.now();
        const delta = now - expectedTime.current;
        const elapsedTimeMs = now - startTime.current;
        expectedTime.current += monitorDelayMs;
        clearTimeout(timeOutHandle.current);
        timeOutHandle.current = setTimeout(handleInactivity, Math.max(0, monitorDelayMs - delta));
        if (!activityDetected.current && !timeoutFired.current && elapsedTimeMs >= idleTimeoutMs) {
            timeoutFired.current = true;
            onIdle(elapsedTimeMs, () => {
                timeoutFired.current = false;
                activityDetected.current = false;
                startTime.current = Date.now();
            });
        }
        else if (activityDetected.current && !timeoutFired.current && elapsedTimeMs >= debounceMs) {
            activityDetected.current = false;
            startTime.current = Date.now();
            onActive();
        }
    }, [debounceMs, idleTimeoutMs, monitorDelayMs, onActive, onIdle]);
    React.useEffect(() => {
        timeOutHandle.current = setTimeout(handleInactivity, monitorDelayMs);
        return () => {
            clearInterval(timeOutHandle.current);
        };
    }, [handleInactivity, monitorDelayMs]);
    React.useEffect(() => {
        trackedEvents.forEach((eventName) => window.addEventListener(eventName, handleActivity, true));
        return () => {
            trackedEvents.forEach((eventName) => window.removeEventListener(eventName, handleActivity, true));
        };
    }, [handleActivity, handleInactivity]);
}
